import * as React from 'react';
import { compose } from 'redux';

import type { FieldReportFullViewFilterContextType } from 'af-root/context/fieldReportFullViewFilterContext';

import type { UserTypeFilterOption } from 'ab-enums/userTypeFilter.enum';

import { Button } from '@acceligentllc/storybook';

import SingleOption from 'af-components/Table/Filters/AdditionalFilter/SingleOption';

import type { ADDITIONAL_FILTERS_OPTIONS } from '..';
import { ADDITIONAL_FILTERS, MAIN_FILTER_OPTION_IDS } from '..';
import AssignmentFilter from './AssignmentFilter';
import type { CrewTypeFilterOption } from './CrewTypeFilter';
import CrewTypeFilter from './CrewTypeFilter';
import type { JobFilterOption } from './JobFilter';
import JobFilter from './JobFilter';
import type { ProjectManagerFilterOption } from './ProjectManagerFilter';
import ProjectManagerFilter from './ProjectManagerFilter';
import type { ReviewStatusFilterOption } from './ReviewStatusFilter';
import ReviewStatusFilter from './ReviewStatusFilter';
import styles from './styles.module.scss';
import type { SuperintendentFilterOption } from './SuperintendentFilter';
import SuperintendentFilter from './SuperintendentFilter';
import type { WorkSummaryStatusFilterOption } from './WorkSummaryStatusFilter';
import WorkSummaryStatusFilter from './WorkSummaryStatusFilter';

interface OwnProps {
	changeAssignmentsFilter: (filter: UserTypeFilterOption) => void;
	changeCrewTypeFilters: (crewTypeOptions: CrewTypeFilterOption[]) => void;
	changeJobFilters: (jobOptions: JobFilterOption[]) => void;
	changeProjectManagerFilters: (projectManagerOptions: ProjectManagerFilterOption[]) => void;
	changeReviewStatusFilters: (reviewStatusOptions: ReviewStatusFilterOption[]) => void;
	changeSuperintendentFilters: (superintendentOptions: SuperintendentFilterOption[]) => void;
	selectedCrewTypeFilters: CrewTypeFilterOption[];
	selectedJobFilters: JobFilterOption[];
	selectedProjectManagerFilters: ProjectManagerFilterOption[];
	selectedSuperintendentFilters: SuperintendentFilterOption[];
	selectedMainFilterId: number;
	assignmentFilter: Nullable<UserTypeFilterOption>;
	changeWorkSummaryStatusFilters: (statusOptions: WorkSummaryStatusFilterOption[]) => void;
	selectedWorkSummaryStatusFilters: WorkSummaryStatusFilterOption[];
	selectedReviewStatusFilters: ReviewStatusFilterOption[];
	fieldReportFullViewFilterContext: FieldReportFullViewFilterContextType;
	activeAdditionalFilters: typeof ADDITIONAL_FILTERS_OPTIONS;
	resetAdditionalFilters: () => void;
	autoOpenFilters: boolean;
	clearReviewStatusFilters: () => void;
	clearWorkSummaryStatusFilters: () => void;
	clearCrewTypeFilters: () => void;
	clearJobFilters: () => void;
	clearProjectManagerFilters: () => void;
	clearSuperintendentFilters: () => void;
	clearAssignmentsFilter: () => void;
	clearRequiresMyAttentionFilter: () => void;
	clearExcludeCancelled: () => void;
}

type Props = OwnProps;

const FullTableAdditionalFilters: React.FC<Props> = (props) => {

	const {
		changeAssignmentsFilter,
		changeCrewTypeFilters,
		changeJobFilters,
		changeProjectManagerFilters,
		changeReviewStatusFilters,
		changeSuperintendentFilters,
		selectedCrewTypeFilters,
		selectedJobFilters,
		selectedProjectManagerFilters,
		selectedSuperintendentFilters,
		selectedMainFilterId,
		assignmentFilter,
		changeWorkSummaryStatusFilters,
		selectedWorkSummaryStatusFilters,
		selectedReviewStatusFilters,
		fieldReportFullViewFilterContext,
		activeAdditionalFilters,
		resetAdditionalFilters,
		autoOpenFilters,
		clearReviewStatusFilters,
		clearWorkSummaryStatusFilters,
		clearCrewTypeFilters,
		clearJobFilters,
		clearProjectManagerFilters,
		clearSuperintendentFilters,
		clearAssignmentsFilter,
		clearRequiresMyAttentionFilter,
		clearExcludeCancelled,
	} = props;

	const renderFilter = React.useCallback((option: typeof ADDITIONAL_FILTERS_OPTIONS[0]) => {
		if (option.id === ADDITIONAL_FILTERS.JOB && selectedMainFilterId === MAIN_FILTER_OPTION_IDS.JOB) {
			return null;
		}

		switch (option.id) {
			case ADDITIONAL_FILTERS.JOB:
				return (
					<JobFilter
						autoOpen={autoOpenFilters}
						fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
						key={ADDITIONAL_FILTERS.JOB}
						onChange={changeJobFilters}
						onClear={clearJobFilters}
						selected={selectedJobFilters}
					/>
				);
			case ADDITIONAL_FILTERS.PROJECT_MANAGER:
				return (
					<ProjectManagerFilter
						autoOpen={autoOpenFilters}
						fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
						key={ADDITIONAL_FILTERS.PROJECT_MANAGER}
						onChange={changeProjectManagerFilters}
						onClear={clearProjectManagerFilters}
						selected={selectedProjectManagerFilters}
					/>
				);
			case ADDITIONAL_FILTERS.SUPERINTENDENT:
				return (
					<SuperintendentFilter
						autoOpen={autoOpenFilters}
						fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
						key={ADDITIONAL_FILTERS.SUPERINTENDENT}
						onChange={changeSuperintendentFilters}
						onClear={clearSuperintendentFilters}
						selected={selectedSuperintendentFilters}
					/>
				);
			case ADDITIONAL_FILTERS.ASSIGNMENT:
				return (
					<AssignmentFilter
						autoOpen={autoOpenFilters}
						key={ADDITIONAL_FILTERS.ASSIGNMENT}
						onChange={changeAssignmentsFilter}
						onClear={clearAssignmentsFilter}
						selected={assignmentFilter}
					/>
				);
			case ADDITIONAL_FILTERS.CREW_TYPE:
				return (
					<CrewTypeFilter
						autoOpen={autoOpenFilters}
						fieldReportFullViewFilterContext={fieldReportFullViewFilterContext}
						key={ADDITIONAL_FILTERS.CREW_TYPE}
						onChange={changeCrewTypeFilters}
						onClear={clearCrewTypeFilters}
						selected={selectedCrewTypeFilters}
					/>
				);
			case ADDITIONAL_FILTERS.REVIEW_STATUS:
				return (
					<ReviewStatusFilter
						autoOpen={autoOpenFilters}
						key={ADDITIONAL_FILTERS.REVIEW_STATUS}
						onChange={changeReviewStatusFilters}
						onClear={clearReviewStatusFilters}
						selected={selectedReviewStatusFilters}
					/>
				);
			case ADDITIONAL_FILTERS.WORK_SUMMARY_STATUS:
				return (
					<WorkSummaryStatusFilter
						autoOpen={autoOpenFilters}
						key={ADDITIONAL_FILTERS.WORK_SUMMARY_STATUS}
						onChange={changeWorkSummaryStatusFilters}
						onClear={clearWorkSummaryStatusFilters}
						selected={selectedWorkSummaryStatusFilters}
					/>
				);
			case ADDITIONAL_FILTERS.REQUIRES_MY_ATTENTION:
				return (
					<SingleOption label="Requires my attention" onClose={clearRequiresMyAttentionFilter} />
				);
			case ADDITIONAL_FILTERS.EXCLUDE_CANCELLED:
				return (
					<SingleOption label="Exclude cancelled Field Reports" onClose={clearExcludeCancelled} />
				);
			default:
				return null;
		}
	}, [
		changeAssignmentsFilter,
		changeCrewTypeFilters,
		changeJobFilters,
		changeProjectManagerFilters,
		changeReviewStatusFilters,
		changeSuperintendentFilters,
		selectedCrewTypeFilters,
		selectedJobFilters,
		selectedMainFilterId,
		selectedProjectManagerFilters,
		selectedReviewStatusFilters,
		selectedSuperintendentFilters,
		clearAssignmentsFilter,
		clearCrewTypeFilters,
		clearJobFilters,
		clearProjectManagerFilters,
		clearReviewStatusFilters,
		clearSuperintendentFilters,
		clearRequiresMyAttentionFilter,
		autoOpenFilters,
		changeWorkSummaryStatusFilters,
		clearWorkSummaryStatusFilters,
		selectedWorkSummaryStatusFilters,
		fieldReportFullViewFilterContext,
		assignmentFilter,
		clearExcludeCancelled,
	]);

	return (
		<>
			{activeAdditionalFilters.map(renderFilter)}
			{
				!!activeAdditionalFilters.length &&
				<Button customStyleClass={styles.resetButton} label="Reset" onClick={resetAdditionalFilters} style="link" />
			}
		</>
	);
};

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo
);

export default enhance(FullTableAdditionalFilters);
